import { useState } from "react";
import { oldestDeviceId, formatDateTime, type DeviceInfo } from "@/domain/device_info";
import type { DeviceDoc } from "@/domain/session";
import "./account.css";

/**
 * DeviceLimitPanel — E-3(PC)·E-4(MO) 기기 3대 가득 → 하나 해제 후 등록(12 설계 §7-3).
 * 로그인 카드 안 단계(라우트 없음). 기본 선택 = 마지막 접속이 가장 오래된 기기.
 */
export function DeviceLimitPanel({
  devices,
  thisDevice,
  onRelease,
  onCancel,
}: {
  devices: DeviceDoc[];
  thisDevice: DeviceInfo;
  onRelease: (deviceId: string) => Promise<void>;
  onCancel: () => Promise<void>;
}) {
  const [pick, setPick] = useState<string | null>(() => oldestDeviceId(devices));
  const [busy, setBusy] = useState<"release" | "cancel" | null>(null);
  const [err, setErr] = useState("");

  async function release() {
    if (!pick) {
      setErr("해제할 기기를 고르세요.");
      return;
    }
    setErr("");
    setBusy("release");
    try {
      await onRelease(pick);
    } catch (e) {
      const code = (e as { code?: string }).code;
      setErr(
        code === "permission-denied"
          ? "기기를 해제할 권한이 없습니다. 관리자에게 문의하세요."
          : `기기를 해제하지 못했습니다: ${(e as Error).message}`,
      );
      setBusy(null);
    }
  }

  async function cancel() {
    setBusy("cancel");
    try {
      await onCancel();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="ma-limit" role="group" aria-labelledby="ma-limit-title">
      <h2 className="ma-limit-title" id="ma-limit-title">
        등록 기기가 {devices.length}대입니다
      </h2>
      <p className="ma-limit-sub">하나를 해제하고 이 기기를 등록하세요.</p>
      <div className="ma-limit-this">
        이 기기: {thisDevice.browser} · {thisDevice.os} (새 기기)
      </div>
      <div className="ma-limit-list" role="radiogroup" aria-label="해제할 기기">
        {devices.map((d) => (
          <label key={d.id} className={`ma-limit-row${pick === d.id ? " is-on" : ""}`}>
            <input
              type="radio"
              name="ma-limit-pick"
              checked={pick === d.id}
              onChange={() => setPick(d.id)}
              disabled={busy !== null}
            />
            <span className="ma-limit-row-text">
              <span className="ma-limit-row-name">{d.name}</span>
              <span className="ma-help">
                {d.browser} · {d.os} · 마지막 접속 {formatDateTime(d.last_seen_at)}
              </span>
            </span>
          </label>
        ))}
      </div>
      <p className="ma-help">해제한 기기는 다음 접속 때 다시 로그인해야 합니다. 홈 화면 앱과 Safari 는 다른 기기로 셉니다.</p>
      <div className="ma-form-err" role="alert">{err}</div>
      <div className="ma-limit-actions">
        <button type="button" className="btn-ghost" onClick={() => void cancel()} disabled={busy !== null}>
          {busy === "cancel" ? "취소 중…" : "취소 (로그아웃)"}
        </button>
        <button type="button" className="btn-primary" onClick={() => void release()} disabled={busy !== null || !pick}>
          {busy === "release" ? "등록 중…" : "선택한 기기 해제 후 등록"}
        </button>
      </div>
      {thisDevice.kind === "pc" && (
        <p className="ma-limit-tip">공용 PC라면 취소 후 [공용 PC입니다]를 체크하고 다시 로그인하세요(기기로 등록하지 않음).</p>
      )}
    </div>
  );
}
